import type { BatchPatchRequest, BatchPatchResponse } from '@/types/dag'

/**
 * Serializes and coalesces DAG cell saves for a single plan version.
 *
 * Every PATCH /table/rows recomputes the whole version server-side, so firing
 * one request per keystroke-commit (Tab/Enter through a row) produced bursts of
 * 10–30 concurrent PATCHes that deadlocked in SQL Server. The queue keeps at
 * most one request in flight; edits made meanwhile are merged per row (last
 * write wins per cell) and sent as a single batch once the previous one
 * settles. `expected_version` is resolved at send time so consecutive edits to
 * the same row don't trip the optimistic-concurrency check with a stale
 * version.
 */

export interface QueuedCell {
  columnId: string
  value: unknown
}

export interface QueuedRowPatch {
  rowId: string
  rowVersion: number
  cells: QueuedCell[]
}

/** Context of one caller's enqueue, handed back when its batch settles. */
export interface QueuedEdit<TMeta> {
  patches: QueuedRowPatch[]
  meta: TMeta
}

export interface DagPatchQueueOptions<TMeta> {
  send: (payload: BatchPatchRequest) => Promise<BatchPatchResponse>
  getUserId: () => string
  onSuccess: (data: BatchPatchResponse, edits: QueuedEdit<TMeta>[]) => void
  onError: (error: Error, edits: QueuedEdit<TMeta>[]) => void
}

interface BufferedRow {
  rowVersion: number
  cells: Map<string, unknown>
}

export class DagPatchQueue<TMeta> {
  private readonly options: DagPatchQueueOptions<TMeta>
  private buffer = new Map<string, BufferedRow>()
  private bufferedEdits: QueuedEdit<TMeta>[] = []
  /** Latest row_version the server returned to us, per row. */
  private knownVersions = new Map<string, number>()
  private inFlight = false

  constructor(options: DagPatchQueueOptions<TMeta>) {
    this.options = options
  }

  enqueue(patches: QueuedRowPatch[], meta: TMeta): void {
    if (patches.length === 0) return

    for (const patch of patches) {
      const row = this.buffer.get(patch.rowId)
      if (row) {
        row.rowVersion = Math.max(row.rowVersion, patch.rowVersion)
        for (const cell of patch.cells) row.cells.set(cell.columnId, cell.value)
      } else {
        this.buffer.set(patch.rowId, {
          rowVersion: patch.rowVersion,
          cells: new Map(patch.cells.map(c => [c.columnId, c.value])),
        })
      }
    }
    this.bufferedEdits.push({ patches, meta })

    if (!this.inFlight) void this.flush()
  }

  /** Records a version learned out-of-band (e.g. SSE row:update). */
  noteVersion(rowId: string, version: number): void {
    const known = this.knownVersions.get(rowId)
    if (known === undefined || version > known) {
      this.knownVersions.set(rowId, version)
    }
  }

  private async flush(): Promise<void> {
    this.inFlight = true
    try {
      while (this.buffer.size > 0) {
        await this.sendBuffered()
      }
    } finally {
      this.inFlight = false
    }
  }

  private async sendBuffered(): Promise<void> {
    const rows = this.buffer
    const edits = this.bufferedEdits
    this.buffer = new Map()
    this.bufferedEdits = []

    const payload: BatchPatchRequest = {
      patches: Array.from(rows, ([rowId, row]) => ({
        row_id: rowId,
        cells: Array.from(row.cells, ([column_id, value]) => ({
          column_id,
          value,
        })),
        expected_version: Math.max(
          row.rowVersion,
          this.knownVersions.get(rowId) ?? row.rowVersion
        ),
      })),
      user_id: this.options.getUserId(),
    }

    let data: BatchPatchResponse
    try {
      data = await this.options.send(payload)
    } catch (error) {
      this.options.onError(
        error instanceof Error ? error : new Error(String(error)),
        edits
      )
      return
    }

    for (const patch of data.patches) {
      this.noteVersion(patch.row_id, patch.new_version)
    }
    this.options.onSuccess(data, edits)
  }
}
